import React from 'react';
import { ProjectResults as ProjectResultsType } from '../types';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

interface ProjectResultsProps {
  results: ProjectResultsType;
}

const ProjectResults = ({ results }: ProjectResultsProps) => {
  return (
    <div className="bg-surface rounded-xl p-6 lg:p-8 elevation-2 mb-8">
      <h2 className="text-2xl font-bold text-text-primary mb-6">Results & Impact</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Summary and Metrics */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
              <Icon name="TrendingUp" size={20} className="text-accent mr-2" />
              Project Impact
            </h3>
            <p className="text-text-secondary leading-relaxed mb-6">
              {results.summary}
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              Performance Metrics
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {results.metrics.map((metric, index) => (
                <div key={index} className="bg-muted rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-accent mb-1">
                    {metric.value}
                  </div>
                  <div className="text-sm text-text-secondary mb-2">
                    {metric.label}
                  </div>
                  {metric.improvement && (
                    <div className="text-xs text-green-600 flex items-center justify-center">
                      <Icon name="TrendingUp" size={12} className="mr-1" />
                      {metric.improvement}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
              <Icon name="Award" size={20} className="text-accent mr-2" />
              Key Achievements
            </h3>
            <ul className="space-y-2">
              {results.achievements.map((achievement, index) => (
                <li key={index} className="flex items-start">
                  <Icon name="CheckCircle" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-text-secondary">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Testimonial */}
        {results.testimonial && (
          <div className="bg-muted rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Icon name="Quote" size={24} className="text-accent mr-3" />
              <h3 className="text-lg font-semibold text-text-primary">
                Client Testimonial
              </h3>
            </div>
            
            <blockquote className="text-text-secondary italic leading-relaxed mb-6">
              "{results.testimonial.content}"
            </blockquote>
            
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={results.testimonial.avatar}
                  alt={results.testimonial.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="font-semibold text-text-primary">
                  {results.testimonial.author}
                </div>
                <div className="text-sm text-text-secondary">
                  {results.testimonial.role} at {results.testimonial.company}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectResults;